import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { IAppFeaturesBotDataGrid } from "./IAppFeaturesBotDataGrid";

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalL,
  },
  actions: {
    marginTop: tokens.spacingVerticalL,
  },
  checkbox: {
    padding: "0px",
    margin: "0px"
  }
});

export const AppFeaturesBotDialog: React.FC<{
  open: boolean,
  setOpen: (value: boolean) => void,
  item: IAppFeaturesBotDataGrid,
  onUpdate: (item: IAppFeaturesBotDataGrid) => void
}
> = ({ open, setOpen, item, onUpdate }) => {
  const intl = useIntl();
  const classes = useStyles();

  const [localItem, setLocalItem] = useState<IAppFeaturesBotDataGrid>(item);

  const handleInputChange = (field: keyof IAppFeaturesBotDataGrid, value: string) => {
    const updatedItem = { ...localItem, [field]: value };
    setLocalItem(updatedItem);
  };

  const onConfirm = () => {
    onUpdate(localItem);
    setOpen(false)
  }

  const isValid = localItem.title && localItem.description && localItem.scope?.length > 0;

  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {intl.formatMessage({ defaultMessage: "Bot command details" })}
          </DialogTitle>
          <DialogContent className={classes.container}>
            <Field label={intl.formatMessage({ defaultMessage: "Command" })} required>
              <Input
                placeholder={intl.formatMessage({ defaultMessage: "Enter a command name" })}
                value={localItem.title || ""}
                onChange={(e) => handleInputChange("title", e.target.value)}
              />
            </Field>
            <Field label={intl.formatMessage({ defaultMessage: "Description (help text)" })} required>
              <Input
                placeholder={intl.formatMessage({ defaultMessage: "Enter a description" })}
                value={localItem.description || ""}
                onChange={(e) => handleInputChange("description", e.target.value)}
              />
            </Field>
            <>
              <Field label={intl.formatMessage({ defaultMessage: "Select the scope" })} required />
              <Checkbox
                className={classes.checkbox}
                label={intl.formatMessage({ defaultMessage: "Personal" })}
                checked={localItem.scope?.includes("personal")}
                onChange={(e, data) => {
                  const scopes = [...(localItem.scope ?? [])];
                  if (data.checked) {
                    scopes.push("personal");
                  } else {
                    scopes.splice(scopes.indexOf("personal"), 1);
                  }
                  setLocalItem({ ...localItem, scope: scopes });
                }}
              />
              <Checkbox
                className={classes.checkbox}
                label={intl.formatMessage({ defaultMessage: "Teams" })}
                checked={localItem.scope?.includes("team")}
                onChange={(e, data) => {
                  const scopes = [...(localItem.scope ?? [])];
                  if (data.checked) {
                    scopes.push("team");
                  } else {
                    scopes.splice(scopes.indexOf("team"), 1);
                  }
                  setLocalItem({ ...localItem, scope: scopes });
                }}
              />
              <Checkbox
                className={classes.checkbox}
                label={intl.formatMessage({ defaultMessage: "Group chat" })}
                checked={localItem.scope?.includes("groupChat")}
                onChange={(e, data) => {
                  const scopes = [...(localItem.scope ?? [])];
                  if (data.checked) {
                    scopes.push("groupChat");
                  } else {
                    scopes.splice(scopes.indexOf("groupChat"), 1);
                  }
                  setLocalItem({ ...localItem, scope: scopes });
                }}
              />
            </>
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button appearance="secondary" onClick={() => { setOpen(false) }}>
              {intl.formatMessage({ defaultMessage: "Cancel" })}
            </Button>
            <Button disabled={!isValid} appearance="primary" onClick={onConfirm}>
              {intl.formatMessage({ defaultMessage: "Confirm" })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

